import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { CreditCard, Unlink, ExternalLink, ShieldCheck, Loader2, CheckCircle2 } from 'lucide-react';
import { stripeConnectApi } from '../lib/api';

// Pre-fills the restricted-key creation screen in Stripe with just the access we need.
const STRIPE_CREATE_KEY_URL =
  'https://dashboard.stripe.com/apikeys/create?name=Corbel%20Invoice%20Payments' +
  '&permissions%5Bcheckout_sessions%5D=write&permissions%5Bpayment_intents%5D=read';

export const StripePaymentsSettings = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [keyInput, setKeyInput] = useState('');

  const fetchStatus = async () => {
    try {
      const res = await stripeConnectApi.getStatus();
      setStatus(res.data);
    } catch (e) {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  const handleConnect = async () => {
    const key = keyInput.trim();
    if (!key) {
      toast.error('Paste your Stripe secret key first.');
      return;
    }
    setWorking(true);
    try {
      await stripeConnectApi.connect(key);
      setKeyInput('');
      toast.success('Stripe connected — your invoices can now be paid online.');
      await fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not connect Stripe. Please try again.');
    } finally {
      setWorking(false);
    }
  };

  const handleDisconnect = async () => {
    setWorking(true);
    try {
      await stripeConnectApi.disconnect();
      toast.success('Stripe disconnected');
      await fetchStatus();
    } catch (e) {
      toast.error('Failed to disconnect');
    } finally {
      setWorking(false);
    }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-sm text-muted-foreground p-4"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  }

  const connected = status?.connected;

  return (
    <div className="space-y-4" data-testid="stripe-payments-settings">
      <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-sm">
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 bg-white rounded-sm flex items-center justify-center border">
            <CreditCard className="h-6 w-6 text-[#635bff]" strokeWidth={1.5} />
          </div>
          <div>
            <p className="font-medium">Stripe Payments</p>
            <p className="text-sm text-muted-foreground">Let clients pay your invoices online — money goes straight to your Stripe account</p>
          </div>
        </div>

        {connected && (
          <div className="flex items-center gap-3">
            <div className="text-right">
              <Badge className="bg-green-700" data-testid="stripe-connected-badge">
                <CheckCircle2 className="h-3 w-3 mr-1" /> Connected
              </Badge>
              {status?.mode === 'test' && (
                <Badge variant="outline" className="ml-1 text-amber-700 border-amber-300">Test mode</Badge>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                {status?.account_name ? `${status.account_name} · ` : ''}key ••••{status?.last4}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={handleDisconnect} disabled={working}
              className="rounded-sm" data-testid="stripe-disconnect-btn">
              <Unlink className="h-4 w-4 mr-1" /> Disconnect
            </Button>
          </div>
        )}
      </div>

      {connected ? (
        <p className="text-sm text-muted-foreground">
          Every invoice now shows a secure <span className="font-medium">“Pay Now”</span> button on its shareable link
          and client portal. Clients pay the amount due and funds go directly to your Stripe account — Corbel takes 0%
          (standard Stripe processing fees still apply).
        </p>
      ) : (
        <div className="border rounded-sm p-5 space-y-4">
          <div>
            <p className="font-medium text-sm">Connect your Stripe account in 3 steps</p>
            <p className="text-xs text-muted-foreground mt-1">
              No Stripe Connect, no extra verification — you just paste one key. We store it encrypted and never
              touch your balance.
            </p>
          </div>

          <ol className="space-y-3 text-sm">
            <li className="flex gap-3">
              <span className="flex-none h-5 w-5 rounded-full bg-primary text-white text-xs flex items-center justify-center">1</span>
              <div>
                <p>Open Stripe's "Create restricted key" page.</p>
                <a href={STRIPE_CREATE_KEY_URL} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 text-primary underline mt-1" data-testid="stripe-create-key-link">
                  Create my Stripe key <ExternalLink className="h-3 w-3" />
                </a>
                <div className="text-xs text-muted-foreground mt-2 space-y-1">
                  <p>On that screen you'll see a long list of permissions — leave them all on <span className="font-medium">None</span> except one:</p>
                  <p>• Set <span className="font-medium text-foreground">Checkout Sessions</span> to <span className="font-medium text-foreground">Write</span>.</p>
                  <p>Then click <span className="font-medium">Create key</span>. (That's the only permission we need — we can't touch your balance or payouts.)</p>
                  <p className="pt-1">Prefer the simplest option? You can paste your <span className="font-medium">standard secret key</span> from the API keys page instead — no permission toggles needed.</p>
                </div>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="flex-none h-5 w-5 rounded-full bg-primary text-white text-xs flex items-center justify-center">2</span>
              <p>Copy the key it shows you (it starts with <code className="text-xs bg-secondary px-1 rounded">rk_</code> or <code className="text-xs bg-secondary px-1 rounded">sk_</code>).</p>
            </li>
            <li className="flex gap-3">
              <span className="flex-none h-5 w-5 rounded-full bg-primary text-white text-xs flex items-center justify-center">3</span>
              <div className="flex-1">
                <p className="mb-2">Paste it here and connect.</p>
                <div className="flex gap-2">
                  <Input
                    type="password"
                    placeholder="rk_live_…  or  sk_live_…"
                    value={keyInput}
                    onChange={(e) => setKeyInput(e.target.value)}
                    className="rounded-sm font-mono text-sm"
                    data-testid="stripe-key-input"
                  />
                  <Button onClick={handleConnect} disabled={working} className="rounded-sm flex-none" data-testid="stripe-connect-btn">
                    {working ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Connecting…</> : 'Connect & Test'}
                  </Button>
                </div>
              </div>
            </li>
          </ol>

          <div className="flex items-start gap-2 text-xs text-muted-foreground bg-secondary/40 p-3 rounded-sm">
            <ShieldCheck className="h-4 w-4 flex-none mt-0.5 text-green-700" />
            <span>
              Your key is encrypted in our database and only used to create payment pages for your invoices. We can’t
              move money out of your account or see your balance. You can disconnect anytime.
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
